import { useAuth0 } from '@auth0/auth0-react';
import { useModifyEntityService } from 'customHooks/useModifyEntityService';
import DataSetOperators from 'interfaces/DataSetOperators';
import DataWrapper from 'interfaces/DataWrapper';
import Identifiable from 'interfaces/Identifiable';
import { Entities } from 'models/Entities';
import { HttpMethod } from 'models/HttpMethod';
import { LogLevel } from 'models/LogLevel';
import { ApiService } from 'services/ApiService';

export const useEntitySetupHandler = <T extends Identifiable>(
  baseUrl: string,
  sendLog: (message: string, logLevel: LogLevel, data?: object) => void,
  onTokenExpired: () => void,
  onInitialLoad: (entity: T) => void,
  apiEndpoint: Entities,
) => {
  const { createEntity, updateEntity } = useModifyEntityService<T>(
    baseUrl,
    apiEndpoint as string,
    sendLog,
    onTokenExpired,
  );
  const { getAccessTokenSilently } = useAuth0();

  const scheduleService = new ApiService<T>(baseUrl, apiEndpoint as string);

  let accessToken: string;
  const setAccessToken = (token: string) => {
    accessToken = token;
    scheduleService.setAccessToken(accessToken);
  };

  const obtainTokenSilently = async () => {
    const token = await getAccessTokenSilently();
    if (!token) {
      onTokenExpired();
      return;
    }
    scheduleService.setAccessToken(token);
  };

  const getSingleEntityByQuery = async (operators: DataSetOperators) => {
    await obtainTokenSilently();
    let entities = await scheduleService.all(operators);
    if (Array.isArray(entities)) {
      if (entities.length > 0) {
        onInitialLoad(entities[0]);
      }
    } else {
      entities = entities as DataWrapper<T>;
      if (entities.data.length > 0) {
        onInitialLoad(entities.data[0]);
      }
    }
  };

  const executeAction = async (actionUrlWithParameters: string) => {
    await obtainTokenSilently();
    const result = await scheduleService.execute(actionUrlWithParameters, HttpMethod.GET);
    return result;
  };

  const createOrUpdateEntity = async (entityName: string, entity: T): Promise<T | void> => {
    let updatedEntity: T | void = entity;
    if (updatedEntity.id) {
      if (!(await updateEntity(updatedEntity))) {
        throw new Error(`failed to update entity ${entityName} with id ${entity.id}`);
      }
      return updatedEntity;
    }

    updatedEntity = await createEntity(updatedEntity);
    if (!updatedEntity) {
      throw new Error(`Failed to create entity ${entityName}`);
    }
    return updatedEntity;
  };

  return {
    setAccessToken,
    getSingleEntityByQuery,
    createOrUpdateEntity,
    executeAction,
  };
};
